import React, { useState, useEffect } from 'react';
import AddDoctorForm from './Doctor/AddDoctorForm';
import EditDoctorForm from './Doctor/EditDoctorForm';
import DoctorDetailsModal from './Doctor/DoctorDetailsModal';
import DoctorsTable from './Doctor/DoctorsTable';
import axios from '../utils/api';

const Doctors = () => {
  const [doctors, setDoctors] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [viewingDoctor, setViewingDoctor] = useState(null);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [showAddSpecialtyModal, setShowAddSpecialtyModal] = useState(false);
  const [newSpecialty, setNewSpecialty] = useState({ name: '', description: '' });
  const [editingSpecialty, setEditingSpecialty] = useState(null);
  const [showEditSpecialtyModal, setShowEditSpecialtyModal] = useState(false);

  const fetchDoctors = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/doctors');
      setDoctors(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch doctors. ' + (err.response?.data?.message || err.message));
      console.error('Error fetching doctors:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSpecialties = async () => {
    try {
      const response = await axios.get('/api/specialties');
      setSpecialties(response.data);
    } catch (err) {
      console.error('Error fetching specialties:', err);
    }
  };

  useEffect(() => {
    fetchDoctors();
    fetchSpecialties();
  }, []);

  const handleAddDoctor = async (doctorData) => {
    try {
      setLoading(true);
      const response = await axios.post('/api/doctors', doctorData);
      setDoctors([...doctors, response.data]);
      setShowForm(false);
      setError(null);
    } catch (err) {
      setError('Failed to add doctor. ' + (err.response?.data?.message || err.message));
      console.error('Error adding doctor:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDoctor = async (id) => {
    if (window.confirm('Are you sure you want to delete this doctor?')) {
      try {
        setLoading(true);
        await axios.delete(`/api/doctors/${id}`);
        setDoctors(doctors.filter(doctor => doctor._id !== id));
        setError(null);
      } catch (err) {
        setError('Failed to delete doctor. ' + (err.response?.data?.message || err.message));
        console.error('Error deleting doctor:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleViewDoctor = (doctor) => {
    setViewingDoctor(doctor);
  };

  const closeViewModal = () => {
    setViewingDoctor(null);
  };

  const handleEditDoctor = (doctor) => {
    setEditingDoctor(doctor);
  };

  const closeEditModal = () => {
    setEditingDoctor(null);
  };

  const saveEditedDoctor = async (id, doctorData) => {
    try {
      setLoading(true);
      const response = await axios.put(`/api/doctors/${id}`, doctorData);
      
      // Update the doctors array with the edited doctor
      setDoctors(doctors.map(doctor => 
        doctor._id === id ? response.data : doctor
      ));
      
      setEditingDoctor(null);
      setError(null);
    } catch (err) {
      setError('Failed to update doctor. ' + (err.response?.data?.message || err.message));
      console.error('Error updating doctor:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredDoctors = doctors.filter(doctor => {
    const matchesSearch = doctor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (doctor.qualification && doctor.qualification.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesSpecialty = selectedSpecialty === '' || 
      (doctor.specialty && 
        (typeof doctor.specialty === 'object' ? 
          doctor.specialty._id === selectedSpecialty : 
          doctor.specialty === selectedSpecialty));
    
    return matchesSearch && matchesSpecialty;
  });

  const handleSpecialtyChange = (e) => {
    setSelectedSpecialty(e.target.value);
  };

  const handleAddSpecialty = async () => {
    if (!newSpecialty.name || !newSpecialty.description) {
      alert('Please provide both name and description for the specialty.');
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.post('/api/specialties', newSpecialty);
      setSpecialties([...specialties, response.data]);
      setNewSpecialty({ name: '', description: '' });
      setShowAddSpecialtyModal(false);
      setError(null);
    } catch (err) {
      setError('Failed to add specialty. ' + (err.response?.data?.message || err.message));
      console.error('Error adding specialty:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSpecialty = (specialty) => {
    setEditingSpecialty({ ...specialty });
    setShowEditSpecialtyModal(true);
  };

  const updateSpecialty = async () => {
    if (!editingSpecialty.name || !editingSpecialty.description) {
      alert('Please provide both name and description for the specialty.');
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.put(`/api/specialties/${editingSpecialty._id}`, editingSpecialty);
      
      // Update the specialties array
      setSpecialties(specialties.map(s => 
        s._id === editingSpecialty._id ? response.data : s
      ));
      
      // Refresh doctors to update specialty names
      fetchDoctors();
      
      setShowEditSpecialtyModal(false);
      setEditingSpecialty(null);
      setError(null);
    } catch (err) {
      setError('Failed to update specialty. ' + (err.response?.data?.message || err.message));
      console.error('Error updating specialty:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Doctors Management</h1>
        <p className="text-gray-600 mt-1">View and manage doctor profiles</p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search doctors by name or qualification..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          {showForm ? 'Cancel' : 'Add New Doctor'}
        </button>
      </div>

      {/* Add Doctor Form */}
      {showForm && (
        <AddDoctorForm
          onAddDoctor={handleAddDoctor}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Specialty Filter and Buttons */}
      <div className="mb-6 flex flex-col sm:flex-row items-center gap-4">
        <div className="w-full sm:w-1/3">
          <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Specialty</label>
          <select
            value={selectedSpecialty}
            onChange={handleSpecialtyChange}
            className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Specialties</option>
            {specialties.map(specialty => (
              <option key={specialty._id} value={specialty._id}>
                {specialty.name}
              </option>
            ))}
          </select>
        </div>
        <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-2 sm:self-end">
          <button
            onClick={() => setShowAddSpecialtyModal(true)}
            className="w-full sm:w-auto inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add New Specialty
          </button>
          {selectedSpecialty && (
            <button
              onClick={() => {
                const specialty = specialties.find(s => s._id === selectedSpecialty);
                if (specialty) {
                  handleEditSpecialty(specialty);
                }
              }}
              className="w-full sm:w-auto inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Selected Specialty
            </button>
          )}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 border-t-2 border-b-2 border-primary-600 rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-600">Loading doctors...</p>
          </div>
        </div>
      )}

      {/* Add Specialty Modal */}
      {showAddSpecialtyModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md mx-auto p-6 w-full">
            <div className="flex justify-between items-start pb-4 border-b mb-4">
              <h3 className="text-xl font-bold text-gray-800">Add New Specialty</h3>
              <button 
                onClick={() => setShowAddSpecialtyModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Specialty Name</label>
                <input
                  type="text"
                  value={newSpecialty.name}
                  onChange={(e) => setNewSpecialty({...newSpecialty, name: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., Cardiology"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={newSpecialty.description}
                  onChange={(e) => setNewSpecialty({...newSpecialty, description: e.target.value})}
                  rows="3"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Describe this medical specialty..."
                  required
                ></textarea>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowAddSpecialtyModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleAddSpecialty}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Add Specialty
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Specialty Modal */}
      {showEditSpecialtyModal && editingSpecialty && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md mx-auto p-6 w-full">
            <div className="flex justify-between items-start pb-4 border-b mb-4">
              <h3 className="text-xl font-bold text-gray-800">Edit Specialty</h3>
              <button 
                onClick={() => setShowEditSpecialtyModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Specialty Name</label>
                <input
                  type="text"
                  value={editingSpecialty.name}
                  onChange={(e) => setEditingSpecialty({...editingSpecialty, name: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={editingSpecialty.description}
                  onChange={(e) => setEditingSpecialty({...editingSpecialty, description: e.target.value})}
                  rows="3"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
                  required
                ></textarea>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowEditSpecialtyModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={updateSpecialty}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Update Specialty
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Doctor Details Modal */}
      <DoctorDetailsModal 
        doctor={viewingDoctor} 
        onClose={closeViewModal} 
      />

      {/* Edit Doctor Modal */}
      {editingDoctor && (
        <EditDoctorForm
          doctor={editingDoctor}
          onEditDoctor={saveEditedDoctor}
          onCancel={closeEditModal}
        />
      )}

      {/* Doctors Table */}
      {!loading && (
        <DoctorsTable
          doctors={filteredDoctors}
          onViewDoctor={handleViewDoctor}
          onEditDoctor={handleEditDoctor}
          onDeleteDoctor={handleDeleteDoctor}
        />
      )}
    </div>
  );
};

export default Doctors;
